/**
 * O cartão de um plano na vitrine.
 *
 * Três regras que o cartão não negocia:
 *  - **`badge` é dado.** O selo ("Comece por aqui", "Mais escolhido") vem do
 *    servidor. Não existe `if (code === 'black')` aqui — nem poderia: o dia em
 *    que o dono trocar o plano em destaque, ele troca no banco, não num deploy.
 *  - **Preço mensal equivalente e desconto vêm prontos.** Este arquivo não
 *    divide `totalCents` por `months` nem tira porcentagem de nada.
 *  - **`-1` é "Ilimitado" e `0` é "Não incluso".** Nunca `-1`, nunca vazio.
 *
 * A AÇÃO não mora aqui. Quem entra por `/planos` sem conta vai para o cadastro;
 * quem já está dentro do workspace dispara o pedido de verdade. O cartão recebe
 * o que fazer pronto (`acao`) — assim os dois caminhos usam o MESMO cartão e o
 * mesmo rótulo de botão, sem duas listas de texto para desencontrar.
 */
import type { ReactNode } from 'react';
import { Badge, cn, NO_DATA_DASH } from '@mgdlead/ui';
import { Check, Minus } from 'lucide-react';

import {
  descreverRecurso,
  mensalEquivalente,
  NAO_INCLUSO,
  precoDoCiclo,
  rotuloDaChave,
  totalFormatado,
} from './billing-ui';
import type { BillingInterval, CatalogPlan } from '../../lib/api';

/** Quantos recursos o cartão mostra antes de mandar para a tabela comparativa. */
const DESTAQUES = 4;

export interface PlanoCardProps {
  plan: CatalogPlan;
  interval: BillingInterval;
  /** A ordem das linhas — a mesma que o servidor manda para a tabela. */
  featureKeys: readonly string[];
  /** O botão (ou link) que a tela de origem monta. */
  acao: ReactNode;
  /** Marca o plano que o workspace já usa. */
  atual?: boolean;
  /**
   * `true` só onde a tabela comparativa está logo abaixo, na MESMA tela.
   * Mandar o leitor "ver abaixo" numa tela que não tem tabela nenhuma é rótulo
   * afirmando o que a tela não tem — e ninguém reclama, porque parece certo.
   */
  comparativoAbaixo?: boolean;
  className?: string;
}

export function PlanoCard({
  plan,
  interval,
  featureKeys,
  acao,
  atual = false,
  comparativoAbaixo = false,
  className,
}: PlanoCardProps) {
  const preco = precoDoCiclo(plan, interval);
  const destaque = plan.badge !== null;

  return (
    <article
      className={cn(
        'flex h-full flex-col gap-4 rounded-lg border bg-surface p-4',
        destaque || atual ? 'border-primary' : 'border-border',
        className,
      )}
    >
      <header className="flex flex-col gap-2">
        <div className="flex min-h-6 flex-wrap items-center gap-2">
          <h3 className="text-md font-semibold tracking-tight">{plan.name}</h3>
          {/* O selo é texto do servidor — a tela não decide quem é destaque. */}
          {plan.badge !== null ? (
            /*
              `text-foreground` por cima do `tone="accent"`: o tom accent do
              design system pinta o TEXTO com `accent-foreground`, que é quase
              preto — feito para o badge SÓLIDO. No fundo a 15% do tema escuro
              isso vira texto escuro em cima de escuro, e o selo comercial mais
              importante da tela fica ilegível justo no tema padrão do produto.
              A correção de verdade é no @mgdlead/ui, que está fora desta rodada.
            */
            <Badge tone="accent" dot className="text-foreground">
              {plan.badge}
            </Badge>
          ) : null}
          {atual ? (
            <Badge tone="info" icon={<Check aria-hidden="true" />}>
              Seu plano
            </Badge>
          ) : null}
        </div>

        {plan.description !== null ? (
          <p className="text-sm text-muted-foreground">{plan.description}</p>
        ) : null}
      </header>

      <div className="flex flex-col gap-1">
        {preco === null ? (
          /* Ciclo que este plano não oferece: travessão, nunca R$ 0,00. */
          <p className="text-sm text-muted-foreground">
            <span aria-hidden="true" className="nums text-xl text-foreground">
              {NO_DATA_DASH}
            </span>{' '}
            Este plano não é vendido neste ciclo.
          </p>
        ) : (
          <>
            <p className="flex flex-wrap items-baseline gap-1.5">
              <span className="nums text-2xl font-bold tracking-tight">
                {totalFormatado(preco)}
              </span>
              {/* O equivalente mensal é do servidor. A tela imprime. */}
              {preco.months > 1 ? (
                <span className="nums text-sm text-muted-foreground">
                  ({mensalEquivalente(preco)})
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">/mês</span>
              )}
            </p>

            <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>
                {preco.months === 1
                  ? 'cobrado todo mês'
                  : `cobrado a cada ${preco.months} meses`}
              </span>
              {preco.discountPercent !== null ? (
                <Badge tone="success" dot numeric>
                  {preco.discountPercent}% off
                </Badge>
              ) : null}
            </p>
          </>
        )}
      </div>

      <ul className="flex flex-col gap-1.5 border-t border-border pt-3">
        {featureKeys.slice(0, DESTAQUES).map((key) => {
          const valor = descreverRecurso(plan.features[key]);
          return (
            <li key={key} className="flex items-start justify-between gap-3 text-sm">
              <span className="min-w-0 text-muted-foreground">{rotuloDaChave(key)}</span>
              {valor === null ? (
                <span className="nums shrink-0 text-muted-foreground" title="sem dado do servidor">
                  <span aria-hidden="true">{NO_DATA_DASH}</span>
                  <span className="sr-only">sem dado</span>
                </span>
              ) : (
                <span className="nums shrink-0 font-medium">
                  {valor === NAO_INCLUSO ? (
                    <span className="inline-flex items-center gap-1 font-normal text-muted-foreground">
                      <Minus aria-hidden="true" className="size-3.5" />
                      {valor}
                    </span>
                  ) : (
                    valor
                  )}
                </span>
              )}
            </li>
          );
        })}
        {featureKeys.length > DESTAQUES ? (
          <li className="text-xs text-muted-foreground">
            e mais {featureKeys.length - DESTAQUES}{' '}
            {comparativoAbaixo ? 'itens na comparação abaixo' : 'itens neste plano'}
          </li>
        ) : null}
      </ul>

      {/* Ação importante = largura cheia, no celular e no desktop deste cartão. */}
      <div className="mt-auto pt-1 [&>*]:w-full">{acao}</div>
    </article>
  );
}
